import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  Download,
  FileText,
  Filter,
  Search,
  SearchX,
  Wallet,
} from 'lucide-react';
import { api, ErroApi } from '../../../api/cliente';
import {
  FILTRO_VAZIO,
  type AnaliseSped,
  type Facetas,
  type FiltroSped,
  type Orcamento,
  type PaginaNotas,
} from '../../../api/tipos';
import {
  Aviso,
  Botao,
  BotaoLink,
  Campo,
  Cartao,
  Celula,
  cx,
  Esqueleto,
  Etiqueta,
  Girador,
  Indicador,
  Indicadores,
  Paginacao,
  Tabela,
  Td,
  Th,
  useConfirmar,
  Vazio,
} from '../../../componentes/ui';
import {
  chaveLegivel,
  dataHora,
  estimativaDownload,
  numero,
  plural,
  reais,
} from '../../../util/formato';
import { Filtros } from './Filtros';
import { Pagina, TituloPagina } from '../../../componentes/Pagina';
import { ModalRecarga } from '../../../componentes/ModalRecarga';
import {
  MODELO,
  OPERACAO,
  SITUACAO,
  SITUACOES_SEM_XML,
  TIPO_SPED,
} from './rotulos';
import css from './DetalheSped.module.css';

/** Espera antes de recalcular as facetas — evita uma chamada por clique. */
const ATRASO_MS = 300;
const POR_PAGINA = 25;

const VOLTAR = { para: '/painel/sped', rotulo: 'SPED' };

/** Datas do SPED vêm sem fuso (meia-noite UTC): exibe sem converter. */
const dataCurta = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' });

export function DetalheSped() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const confirmar = useConfirmar();

  const [analise, setAnalise] = useState<AnaliseSped | null>(null);
  const [facetas, setFacetas] = useState<Facetas | null>(null);
  const [notas, setNotas] = useState<PaginaNotas | null>(null);
  const [filtro, setFiltro] = useState<FiltroSped>(FILTRO_VAZIO);
  const [pagina, setPagina] = useState(1);
  const [erro, setErro] = useState<string | null>(null);
  const [recalculando, setRecalculando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [orcamento, setOrcamento] = useState<Orcamento | null>(null);
  const [recarregando, setRecarregando] = useState(false);
  /** Sobe a cada Pix confirmado, para o orçamento ser reconsultado. */
  const [saldoVersao, setSaldoVersao] = useState(0);

  /** O filtro vira query string — o servidor conta com ele aplicado. */
  const query = useMemo(() => {
    const p = new URLSearchParams();
    for (const [campo, valor] of Object.entries(filtro)) {
      if (Array.isArray(valor)) {
        for (const v of valor) p.append(campo, v);
      } else if (valor) {
        p.set(campo, valor);
      }
    }
    return p.toString();
  }, [filtro]);

  useEffect(() => {
    if (!id) return;
    api<AnaliseSped>(`/sped/analises/${id}`)
      .then(setAnalise)
      .catch((err: unknown) => {
        setErro(
          err instanceof ErroApi
            ? err.message
            : 'Não foi possível abrir a análise.',
        );
      });
  }, [id]);

  // Recalcula facetas e página de notas quando o filtro muda. O atraso agrupa
  // vários cliques seguidos numa chamada só.
  useEffect(() => {
    if (!id) return;
    setRecalculando(true);
    const t = window.setTimeout(() => {
      const sufixo = query ? `?${query}` : '';
      void Promise.all([
        api<Facetas>(`/sped/analises/${id}/facetas${sufixo}`),
        api<PaginaNotas>(
          `/sped/analises/${id}/notas${sufixo}${sufixo ? '&' : '?'}pagina=${pagina}&porPagina=${POR_PAGINA}`,
        ),
      ])
        .then(([f, n]) => {
          setFacetas(f);
          setNotas(n);
        })
        .catch((err: unknown) => {
          if (err instanceof ErroApi) setErro(err.message);
        })
        .finally(() => setRecalculando(false));
    }, ATRASO_MS);
    return () => clearTimeout(t);
  }, [id, query, pagina]);

  // Orça o custo sempre que a seleção muda: o usuário precisa ver o preço
  // ANTES de montar o filtro inteiro e levar um erro de saldo no fim.
  //
  // `saldoVersao` entra nas dependências para o pagamento feito no modal
  // reconsultar o orçamento: quem decide se o saldo cobre é o servidor, não uma
  // soma feita aqui com o valor que a recarga supostamente creditou.
  useEffect(() => {
    const total = facetas?.total ?? 0;
    if (total === 0) {
      setOrcamento(null);
      return;
    }
    let vivo = true;
    api<Orcamento>(`/creditos/orcamento?chaves=${total}`)
      .then((o) => {
        if (vivo) setOrcamento(o);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [facetas?.total, saldoVersao]);

  const aoMudarFiltro = useCallback((f: FiltroSped) => {
    setFiltro(f);
    // Página 1: manter a página 7 depois de restringir o filtro mostraria uma
    // lista vazia sem explicação.
    setPagina(1);
  }, []);

  const limparFiltros = useCallback(
    () => aoMudarFiltro(FILTRO_VAZIO),
    [aoMudarFiltro],
  );

  async function baixar() {
    if (!id) return;

    // Confirmação com os números na frente: é o ponto em que o saldo sai do
    // disponível, e a regra de estorno só é útil se for lida ANTES, não depois
    // de o cliente estranhar o saldo menor.
    if (orcamento) {
      const ok = await confirmar({
        titulo: `Baixar ${plural(selecionadas, 'XML', 'XMLs')} por ${reais(orcamento.totalCentavos)}?`,
        mensagem: (
          <div className={css.confirmacao}>
            <p>
              <strong>{reais(orcamento.totalCentavos)}</strong> ficam reservados
              do seu saldo enquanto o lote roda.
            </p>
            <p>
              No fim, só é cobrado o que for entregue — nota não encontrada ou
              com erro não é cobrada, e a diferença volta para o saldo.
            </p>
            <p>
              Tempo estimado: {estimativaDownload(selecionadas)}. A busca roda
              em segundo plano; pode fechar a aba.
            </p>
          </div>
        ),
        confirmar: 'Baixar XMLs',
        cancelar: 'Voltar',
      });
      if (!ok) return;
    }

    setErro(null);
    setBaixando(true);
    try {
      const r = await api<{ jobId: string; total: number }>(
        `/sped/analises/${id}/baixar`,
        { metodo: 'POST', corpo: filtro },
      );
      navegar(`/painel/downloads/${r.jobId}`);
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível iniciar o download.',
      );
    } finally {
      setBaixando(false);
    }
  }

  if (erro && !analise) {
    return (
      <Pagina largura="estreita">
        <TituloPagina titulo="Análise não encontrada" voltar={VOLTAR} />
        <Aviso tom="neutro">{erro}</Aviso>
        <div>
          <BotaoLink to="/painel/sped" aparencia="secundario">
            Voltar para SPED
          </BotaoLink>
        </div>
      </Pagina>
    );
  }

  if (!analise) {
    return (
      <Pagina>
        <div className={css.carregando}>
          <Esqueleto altura={14} largura={64} />
          <Esqueleto altura={28} largura={280} />
        </div>
        <Esqueleto altura={96} />
        <Esqueleto altura={240} />
      </Pagina>
    );
  }

  const selecionadas = facetas?.total ?? 0;
  const semXml = (facetas?.situacoes ?? [])
    .filter((s) => SITUACOES_SEM_XML.has(s.situacao))
    .reduce((soma, s) => soma + s.notas, 0);
  const faltaSaldo = orcamento?.suficiente === false;

  return (
    <Pagina>
      <TituloPagina
        titulo={analise.nome}
        descricao={`${plural(analise.totalNotas, 'nota')} com chave, de ${plural(analise.arquivos, 'arquivo')} · ${dataHora(analise.createdAt)}`}
        voltar={VOLTAR}
      />

      {erro && <Aviso>{erro}</Aviso>}

      <Indicadores>
        <Indicador
          rotulo="Notas com chave"
          valor={numero(analise.totalNotas)}
          detalhe={`Em ${plural(analise.arquivos, 'arquivo')}`}
          icone={<FileText />}
        />
        <Indicador
          rotulo="Selecionadas"
          valor={facetas ? numero(selecionadas) : '—'}
          detalhe={
            query === ''
              ? 'Sem filtro: todas incluídas'
              : 'Com os filtros atuais'
          }
          icone={<Filter />}
        />
        <Indicador
          rotulo="Sem XML disponível"
          valor={facetas ? numero(semXml) : '—'}
          detalhe="Canceladas, denegadas ou inutilizadas"
          icone={<AlertTriangle />}
          tom={semXml > 0 ? 'alerta' : undefined}
        />
        <Indicador
          rotulo="Saldo disponível"
          valor={orcamento ? reais(orcamento.saldoCentavos) : '—'}
          detalhe={
            !orcamento
              ? 'Aparece ao selecionar notas'
              : faltaSaldo
                ? `Faltam ${reais(orcamento.faltamCentavos)} para este lote`
                : 'Cobre este lote'
          }
          icone={<Wallet />}
          tom={faltaSaldo ? 'erro' : undefined}
        />
      </Indicadores>

      <ArquivosDaAnalise analise={analise} />

      <Cartao
        titulo="Filtrar notas"
        descricao="Grupo sem nada marcado inclui tudo. A contagem de cada opção já considera os outros filtros."
        acoes={
          <div className={css.acoesFiltro}>
            {recalculando && (
              <span className={css.recalculando} role="status">
                <Girador tamanho={12} /> Recalculando
              </span>
            )}
            <Botao
              aparencia="discreto"
              pequeno
              onClick={limparFiltros}
              disabled={query === ''}
            >
              Limpar filtros
            </Botao>
          </div>
        }
      >
        <div className={css.filtros}>
          <Campo
            rotulo="Busca livre"
            rotuloOculto
            icone={<Search />}
            value={filtro.busca}
            onChange={(e) =>
              aoMudarFiltro({ ...filtro, busca: e.target.value })
            }
            placeholder="Buscar por chave, participante ou CNPJ"
          />

          {facetas ? (
            <Filtros
              facetas={facetas}
              filtro={filtro}
              aoMudar={aoMudarFiltro}
            />
          ) : (
            <Esqueleto altura={200} />
          )}
        </div>
      </Cartao>

      {/* ── Barra de ação: fica fixa no rodapé enquanto se filtra ─────── */}
      <div className={css.barra}>
        <div className={css.resumo}>
          <span className={css.resumoTitulo}>
            {plural(selecionadas, 'nota')}
            {orcamento && (
              <span className={css.resumoValor}>
                {' '}
                · {reais(orcamento.totalCentavos)}
              </span>
            )}
          </span>
          <span className={css.resumoDetalhe}>
            {orcamento
              ? `${reais(orcamento.precoUnitarioCentavos)} por XML · saldo ${reais(orcamento.saldoCentavos)}`
              : 'Sem seleção nos filtros, tudo é incluído.'}
            {semXml > 0 && (
              <span className={css.resumoAlerta}>
                {' '}
                · {plural(
                  semXml,
                  'cancelada/denegada',
                  'canceladas/denegadas',
                )}{' '}
                sem XML
              </span>
            )}
          </span>
          {orcamento && (
            <span className={css.resumoDetalhe}>
              {reais(orcamento.totalCentavos)} ficam reservados; cobramos só os
              XMLs entregues e devolvemos o resto ao saldo.
            </span>
          )}
        </div>
        <div className={css.barraAcoes}>
          {orcamento && !orcamento.suficiente && (
            <Botao
              aparencia="secundario"
              icone={<Wallet />}
              onClick={() => setRecarregando(true)}
            >
              Faltam {reais(orcamento.faltamCentavos)} — recarregar
            </Botao>
          )}
          <Botao
            icone={<Download />}
            onClick={() => void baixar()}
            carregando={baixando}
            // Bloqueia antes de tentar: melhor que deixar o usuário clicar e
            // levar um 400 depois de montar o filtro inteiro.
            disabled={selecionadas === 0 || faltaSaldo}
            title={faltaSaldo ? 'Saldo insuficiente para este lote' : undefined}
          >
            Baixar XMLs
          </Botao>
        </div>
      </div>

      <TabelaNotas
        notas={notas}
        pagina={pagina}
        aoMudarPagina={setPagina}
        filtrado={query !== ''}
        aoLimpar={limparFiltros}
      />

      <ModalRecarga
        aberto={recarregando}
        aoFechar={() => setRecarregando(false)}
        aoConfirmar={() => setSaldoVersao((v) => v + 1)}
        faltamCentavos={
          orcamento && !orcamento.suficiente
            ? orcamento.faltamCentavos
            : undefined
        }
      />
    </Pagina>
  );
}

/* ── Arquivos que compuseram a análise ──────────────────────────────────── */

function ArquivosDaAnalise({ analise }: { analise: AnaliseSped }) {
  const [aberto, setAberto] = useState(false);
  const comAviso = analise.arquivosDetalhe.filter((a) => a.aviso).length;

  return (
    <Cartao padding={0}>
      <button
        type="button"
        className={css.arquivosCabecalho}
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
      >
        <span className={css.arquivosTitulo}>
          {plural(
            analise.arquivosDetalhe.length,
            'arquivo analisado',
            'arquivos analisados',
          )}
          {comAviso > 0 && (
            <Etiqueta tom="alerta">{numero(comAviso)} com aviso</Etiqueta>
          )}
        </span>
        <span className={css.arquivosAlternar}>
          {aberto ? 'Ocultar' : 'Mostrar'}
          <ChevronDown
            aria-hidden="true"
            className={cx(css.seta, aberto && css.setaAberta)}
          />
        </span>
      </button>

      {aberto && (
        <div className={css.arquivosCorpo}>
          <Tabela rotulo="Arquivos analisados">
            <thead>
              <tr>
                <Th>Arquivo</Th>
                <Th>Tipo</Th>
                <Th alinhar="direita">Notas</Th>
              </tr>
            </thead>
            <tbody>
              {analise.arquivosDetalhe.map((a) => {
                const periodo = a.competenciaInicio
                  ? `${dataCurta(a.competenciaInicio)} a ${a.competenciaFim ? dataCurta(a.competenciaFim) : '—'}`
                  : null;
                const empresa = [a.nomeEmpresa, periodo]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <tr key={a.id}>
                    <Td>
                      <Celula
                        principal={a.nomeArquivo}
                        secundaria={
                          empresa || a.aviso ? (
                            <>
                              {empresa}
                              {a.aviso && (
                                <span className={css.arquivoAviso}>
                                  <AlertTriangle aria-hidden="true" />
                                  {a.aviso}
                                </span>
                              )}
                            </>
                          ) : undefined
                        }
                      />
                    </Td>
                    <Td>
                      <Etiqueta tom={a.notas > 0 ? 'ok' : 'neutro'}>
                        {TIPO_SPED[a.tipo] ?? a.tipo}
                      </Etiqueta>
                    </Td>
                    <Td alinhar="direita" className="num">
                      {numero(a.notas)}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        </div>
      )}
    </Cartao>
  );
}

/* ── Amostra das notas selecionadas ─────────────────────────────────────── */

function TabelaNotas({
  notas,
  pagina,
  aoMudarPagina,
  filtrado,
  aoLimpar,
}: {
  notas: PaginaNotas | null;
  pagina: number;
  aoMudarPagina: (p: number) => void;
  filtrado: boolean;
  aoLimpar: () => void;
}) {
  return (
    <Cartao titulo="Notas selecionadas" padding={0}>
      {!notas ? (
        <div className={css.carregandoTabela}>
          <Esqueleto altura={20} />
          <Esqueleto altura={20} />
          <Esqueleto altura={20} />
        </div>
      ) : notas.itens.length === 0 ? (
        <Vazio
          icone={<SearchX />}
          titulo="Nenhuma nota atende aos filtros atuais"
          descricao="Desmarque alguma opção ou limpe a busca para ver mais notas."
          acao={
            filtrado ? (
              <Botao aparencia="secundario" pequeno onClick={aoLimpar}>
                Limpar filtros
              </Botao>
            ) : undefined
          }
        />
      ) : (
        <>
          <Tabela rotulo="Notas selecionadas">
            <thead>
              <tr>
                <Th>Chave de acesso</Th>
                <Th>Emissão</Th>
                <Th>Participante</Th>
                <Th>CFOP</Th>
                <Th>Tipo</Th>
                <Th>Situação</Th>
              </tr>
            </thead>
            <tbody>
              {notas.itens.map((n) => (
                <tr key={n.id}>
                  <Td>
                    <code className={css.chave}>{chaveLegivel(n.chave)}</code>
                  </Td>
                  <Td className={cx('num', css.semQuebra)}>
                    {dataCurta(n.dataDoc)}
                  </Td>
                  <Td>{n.participante ?? '—'}</Td>
                  <Td>
                    <span className={css.mono}>
                      {n.cfops.join(', ') || '—'}
                    </span>
                  </Td>
                  <Td className={cx(css.apoio, css.semQuebra)}>
                    {OPERACAO[n.operacao]} · {MODELO[n.modelo] ?? n.modelo}
                  </Td>
                  <Td>
                    {SITUACOES_SEM_XML.has(n.situacao) ? (
                      <Etiqueta tom="erro">
                        {SITUACAO[n.situacao] ?? n.situacao}
                      </Etiqueta>
                    ) : (
                      <span className={css.apoio}>
                        {SITUACAO[n.situacao] ?? n.situacao}
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
          <Paginacao
            total={notas.total}
            pagina={pagina}
            porPagina={notas.porPagina}
            aoMudar={aoMudarPagina}
            rotulo={notas.total === 1 ? 'nota' : 'notas'}
          />
        </>
      )}
    </Cartao>
  );
}
